<script setup lang="ts">
/**
 * SmartSearchBox
 *
 * A reusable navigation search component with debounced input, grouped dropdown
 * results, keyboard navigation, and loading/empty states.
 *
 * Usage:
 *   <SmartSearchBox :fetch-results="myFetchFn" @navigate="onNavigate" />
 *
 * The component is UI-only — it delegates fetching and navigation to the consumer
 * via props and events.
 */
import { ref, computed, watch, nextTick, onBeforeUnmount, type ComponentPublicInstance } from 'vue';
import InputText from 'primevue/inputtext';
import ProgressSpinner from 'primevue/progressspinner';
import Tag from 'primevue/tag';
import Icon from '../icons/IconComponent.vue';

export type SmartSearchResultItem = {
  index_id: string;
  module_code: string;
  module_label: string;
  feature: string;
  display_title: string;
  display_description: string | null;
  page_url: string;
  tags: string[];
};

export type SmartSearchGroup = {
  module_label: string;
  module_code: string;
  items: SmartSearchResultItem[];
};

export type SmartSearchFetchResult = {
  results: SmartSearchResultItem[];
  total: number;
};

const props = withDefaults(
  defineProps<{
    /** Async function that fetches search results for a given query string. */
    fetchResults: (query: string) => Promise<SmartSearchFetchResult>;
    /** Debounce delay in milliseconds. */
    debounceMs?: number;
    /** Minimum characters before triggering search. */
    minChars?: number;
    /** Placeholder text for the input. */
    placeholder?: string;
    /** Label for the empty state. Accepts a string with {query} or {{query}} placeholder, or a function (query) => string. */
    emptyLabel?: string | ((query: string) => string);
    /** Accessible label for the search icon — pass a translated string from the consuming app. */
    searchIconLabel?: string;
    /** Title/tooltip for the clear button. */
    clearButtonTitle?: string;
    /** Accessible label for the clear icon. */
    clearIconLabel?: string;
  }>(),
  {
    debounceMs: 300,
    minChars: 2,
    placeholder: 'Search pages & features…',
    emptyLabel: 'No results found for \'{query}\'',
    searchIconLabel: 'Search',
    clearButtonTitle: 'Clear',
    clearIconLabel: 'Clear search',
  },
);

const emit = defineEmits<{
  /** Emitted when the user selects a result. Payload is the page_url. */
  navigate: [url: string];
}>();

// ── State ──────────────────────────────────────────────────────────────────
const isOpen = ref(false);
const query = ref('');
const results = ref<SmartSearchResultItem[]>([]);
const isLoading = ref(false);
const highlightedIndex = ref(-1);
const inputRef = ref<ComponentPublicInstance | null>(null);
const dropdownRef = ref<HTMLDivElement | null>(null);

let debounceTimer: ReturnType<typeof setTimeout> | undefined;
let requestId = 0;

// ── Computed ───────────────────────────────────────────────────────────────

/** Group results by module_label, preserving order. */
const groupedResults = computed<SmartSearchGroup[]>(() => {
  const map = new Map<string, SmartSearchGroup>();
  for (const item of results.value) {
    let group = map.get(item.module_code);
    if (!group) {
      group = { module_label: item.module_label, module_code: item.module_code, items: [] };
      map.set(item.module_code, group);
    }
    group.items.push(item);
  }
  return Array.from(map.values());
});

/** Flat list of items for keyboard index navigation. */
const flatItems = computed(() => results.value);

const hasQuery = computed(() => query.value.trim().length >= props.minChars);

const showDropdown = computed(
  () => isOpen.value && hasQuery.value,
);

const emptyMessage = computed(() => {
  const trimmed = query.value.trim();
  if (typeof props.emptyLabel === 'function') {
    return props.emptyLabel(trimmed);
  }
  return props.emptyLabel.replace('{{query}}', trimmed).replace('{query}', trimmed);
});

// ── Search Logic ───────────────────────────────────────────────────────────

function scheduleSearch() {
  clearTimeout(debounceTimer);
  const trimmed = query.value.trim();

  if (trimmed.length < props.minChars) {
    results.value = [];
    isLoading.value = false;
    return;
  }

  isLoading.value = true;
  debounceTimer = setTimeout(async () => {
    const currentRequestId = ++requestId;
    try {
      const data = await props.fetchResults(trimmed);
      if (currentRequestId !== requestId) return;
      results.value = data.results;
    } catch {
      if (currentRequestId !== requestId) return;
      results.value = [];
    } finally {
      if (currentRequestId === requestId) {
        isLoading.value = false;
        highlightedIndex.value = -1;
      }
    }
  }, props.debounceMs);
}

watch(query, () => {
  if (!isOpen.value && query.value.trim().length > 0) isOpen.value = true;
  scheduleSearch();
});

// ── Selection ──────────────────────────────────────────────────────────────

function selectItem(item: SmartSearchResultItem) {
  emit('navigate', item.page_url);
  closeSearch();
}

function closeSearch() {
  isOpen.value = false;
  query.value = '';
  results.value = [];
  highlightedIndex.value = -1;
  clearTimeout(debounceTimer);
}

function openSearch() {
  isOpen.value = true;
  nextTick(() => {
    const el = inputRef.value?.$el as HTMLElement | undefined;
    el?.querySelector('input')?.focus();
  });
}

function toggleSearch() {
  if (isOpen.value) {
    closeSearch();
  } else {
    openSearch();
  }
}

// ── Keyboard Navigation ───────────────────────────────────────────────────

function onKeydown(event: KeyboardEvent) {
  const len = flatItems.value.length;

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      highlightedIndex.value = len > 0 ? (highlightedIndex.value + 1) % len : -1;
      scrollToHighlighted();
      break;
    case 'ArrowUp':
      event.preventDefault();
      highlightedIndex.value = len > 0 ? (highlightedIndex.value - 1 + len) % len : -1;
      scrollToHighlighted();
      break;
    case 'Enter':
      event.preventDefault();
      if (highlightedIndex.value >= 0 && highlightedIndex.value < len) {
        selectItem(flatItems.value[highlightedIndex.value]);
      }
      break;
    case 'Escape':
      event.preventDefault();
      closeSearch();
      break;
  }
}

function scrollToHighlighted() {
  nextTick(() => {
    const el = dropdownRef.value?.querySelector('[data-highlighted="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  });
}

/** Get the flat index for a given result item. */
function getFlatIndex(item: SmartSearchResultItem): number {
  return flatItems.value.indexOf(item);
}

// ── Click Outside ──────────────────────────────────────────────────────────
function onClickOutside(event: MouseEvent) {
  const target = event.target as HTMLElement;
  const wrapper = target.closest('[data-smart-search]');
  if (!wrapper) {
    closeSearch();
  }
}

watch(isOpen, (open) => {
  if (open) {
    document.addEventListener('mousedown', onClickOutside);
  } else {
    document.removeEventListener('mousedown', onClickOutside);
  }
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onClickOutside);
  clearTimeout(debounceTimer);
});

// ── Expose for testing ─────────────────────────────────────────────────────
defineExpose({ openSearch, closeSearch, toggleSearch });
</script>

<template>
  <div
    class="relative flex items-center"
    data-smart-search
    data-testid="smart-search"
  >
    <!-- Search Toggle Button -->
    <button
      v-if="!isOpen"
      type="button"
      class="flex items-center rounded-lg p-2 text-gray-500 hover:bg-gray-100 transition-colors"
      :title="placeholder"
      data-testid="smart-search-toggle"
      @click="toggleSearch"
    >
      <Icon
        name="Search"
        size-class="h-5 w-5"
        color="text-gray-500"
        variant="fill"
        :decorative="false"
        :aria-label="searchIconLabel"
      />
    </button>

    <!-- Expanded Search Input -->
    <transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="w-0 opacity-0"
      enter-to-class="w-72 opacity-100"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="w-72 opacity-100"
      leave-to-class="w-0 opacity-0"
    >
      <div
        v-if="isOpen"
        class="relative flex items-center"
      >
        <span class="absolute left-2.5 z-10 flex items-center pointer-events-none">
          <Icon
            name="Search"
            size-class="h-4 w-4"
            color="text-gray-400"
            variant="fill"
            decorative
          />
        </span>

        <InputText
          ref="inputRef"
          v-model="query"
          type="text"
          :placeholder="placeholder"
          class="w-72 pl-9 pr-8 py-2 text-sm rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
          data-testid="smart-search-input"
          autocomplete="off"
          @keydown="onKeydown"
        />

        <!-- Loading spinner or close button in trailing position -->
        <span class="absolute right-2 z-10 flex items-center">
          <ProgressSpinner
            v-if="isLoading"
            style="width: 16px; height: 16px"
            stroke-width="4"
            animation-duration="0.8s"
            data-testid="smart-search-spinner"
          />
          <button
            v-else-if="query.length > 0"
            type="button"
            class="flex items-center text-gray-400 hover:text-gray-600 transition-colors"
            :title="clearButtonTitle"
            data-testid="smart-search-clear"
            @click="closeSearch"
          >
            <Icon
              name="Close"
              size-class="h-4 w-4"
              color="text-gray-600"
              variant="fill"
              :decorative="false"
              :aria-label="clearIconLabel"
            />
          </button>
        </span>
      </div>
    </transition>

    <!-- Dropdown Results -->
    <transition
      enter-active-class="transition-all duration-150 ease-out"
      enter-from-class="opacity-0 -translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition-all duration-100 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <div
        v-if="showDropdown && !isLoading"
        ref="dropdownRef"
        class="absolute top-full right-0 mt-1 w-96 max-h-96 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg z-50"
        role="listbox"
        data-testid="smart-search-dropdown"
      >
        <!-- Empty state -->
        <div
          v-if="results.length === 0"
          class="px-4 py-6 text-center text-sm text-gray-500"
          data-testid="smart-search-empty"
        >
          <Icon
            name="Search"
            size-class="h-8 w-8"
            color="text-gray-300"
            variant="fill"
            decorative
            class="mx-auto mb-2"
          />
          <p>{{ emptyMessage }}</p>
        </div>

        <!-- Grouped results -->
        <template v-else>
          <div
            v-for="group in groupedResults"
            :key="group.module_code"
            class="border-b border-gray-100 last:border-b-0"
          >
            <!-- Group header -->
            <div class="px-3 py-2 bg-gray-50">
              <Tag
                :value="group.module_label"
                severity="secondary"
                class="text-xs"
              />
            </div>

            <!-- Group items -->
            <button
              v-for="item in group.items"
              :key="item.index_id"
              type="button"
              role="option"
              :aria-selected="highlightedIndex === getFlatIndex(item)"
              :data-highlighted="highlightedIndex === getFlatIndex(item)"
              class="w-full text-left px-4 py-2.5 cursor-pointer transition-colors"
              :class="[
                highlightedIndex === getFlatIndex(item)
                  ? 'bg-primary-50 text-primary-900'
                  : 'hover:bg-gray-50 text-gray-900',
              ]"
              data-testid="smart-search-result-item"
              @click="selectItem(item)"
              @mouseenter="highlightedIndex = getFlatIndex(item)"
            >
              <div class="font-semibold text-sm leading-tight">
                {{ item.display_title }}
              </div>
              <div
                v-if="item.display_description"
                class="text-xs text-gray-500 mt-0.5 line-clamp-1"
              >
                {{ item.display_description }}
              </div>
            </button>
          </div>
        </template>
      </div>
    </transition>
  </div>
</template>
